/**
 * src/components/viewer/LightboxViewer.tsx
 *
 * Production-ready Level-2 Fullscreen Lightbox component for Polarsteps Travel Photo Map.
 * Built with PhotoSwipe v5.4.4 in React 18 with touch physics, progressive loading,
 * vertical swipe-to-close, and overlay hooks for EXIF Drawer and Nearby Filmstrip.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PhotoSwipe, { type PhotoSwipeOptions } from 'photoswipe';
import 'photoswipe/dist/photoswipe.css';
import { PhotoRecord } from '@/types/domain';
import { storageService, sortPhotosChronologically } from '@/services/storage';
import { X, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { ExifDrawer } from './ExifDrawer';
import { Filmstrip } from './Filmstrip';

export interface LightboxViewerProps {
  /** Filtered list of photos currently displayed on the map */
  photos: PhotoRecord[];
  /** Initial photo ID or numeric index to open */
  initialPhotoId?: string;
  initialIndex?: number;
  /** Lightbox visibility flag */
  isOpen: boolean;
  /** Callback fired when user closes the lightbox */
  onClose: () => void;
  /** Callback fired when the active slide changes during swiping */
  onPhotoChange?: (photo: PhotoRecord, index: number) => void;
  /** Optional custom URL resolver; defaults to storageService.getPhotoUrl */
  urlResolver?: (key: string) => Promise<string | undefined>;
  /** Render prop for Translucent EXIF Drawer (Explorer 2) */
  renderExifDrawer?: (
    currentPhoto: PhotoRecord,
    isDrawerOpen: boolean,
    onToggleDrawer: () => void
  ) => React.ReactNode;
  /** Render prop for Nearby Photos Filmstrip (Explorer 2) */
  renderFilmstrip?: (
    currentIndex: number,
    photos: PhotoRecord[],
    onSelectIndex: (index: number) => void
  ) => React.ReactNode;
  timezoneId?: string;
  onLocateOnMap?: (photo: PhotoRecord) => void;
  /** Optional CSS class name for root container */
  className?: string;
}

export const LightboxViewer: React.FC<LightboxViewerProps> = ({
  photos,
  initialPhotoId,
  initialIndex = 0,
  isOpen,
  onClose,
  onPhotoChange,
  urlResolver,
  renderExifDrawer,
  renderFilmstrip,
  timezoneId,
  onLocateOnMap,
  className = '',
}) => {
  const pswpRef = useRef<PhotoSwipe | null>(null);
  const isInitializingRef = useRef<boolean>(false);
  const activeBlobUrlsRef = useRef<Set<string>>(new Set());

  // Ensure photos are strictly sorted in chronological order (shot timestamp)
  const sortedPhotos = useMemo(() => {
    return sortPhotosChronologically(photos);
  }, [photos]);

  // Resolved index within the sortedPhotos collection
  const resolvedStartIndex = useMemo(() => {
    if (initialPhotoId) {
      const idx = sortedPhotos.findIndex((p) => p.id === initialPhotoId);
      if (idx !== -1) return idx;
    }
    if (initialIndex >= 0 && initialIndex < sortedPhotos.length) {
      return initialIndex;
    }
    return 0;
  }, [sortedPhotos, initialPhotoId, initialIndex]);

  const [currentIndex, setCurrentIndex] = useState<number>(resolvedStartIndex);
  const [isExifDrawerOpen, setIsExifDrawerOpen] = useState<boolean>(false);
  const [controlsVisible, setControlsVisible] = useState<boolean>(true);

  // Synchronize currentIndex if resolvedStartIndex changes while lightbox is mounted
  useEffect(() => {
    setCurrentIndex(resolvedStartIndex);
  }, [resolvedStartIndex]);

  // Stable callbacks for external consumption
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onPhotoChangeRef = useRef(onPhotoChange);
  onPhotoChangeRef.current = onPhotoChange;

  // Asynchronous URL fetcher helper
  const resolveUrl = useCallback(
    async (key: string): Promise<string | undefined> => {
      if (!key) return undefined;
      if (
        key.startsWith('http://') ||
        key.startsWith('https://') ||
        key.startsWith('data:') ||
        key.startsWith('blob:')
      ) {
        return key;
      }
      if (urlResolver) {
        return await urlResolver(key);
      }
      return await storageService.getPhotoUrl(key);
    },
    [urlResolver]
  );

  // Build PhotoSwipe slide data array from PhotoRecord[]
  const slideItems = useMemo(() => {
    return sortedPhotos.map((p, idx) => {
      const isDirectUrl = (s?: string) =>
        Boolean(
          s &&
            (s.startsWith('http://') ||
              s.startsWith('https://') ||
              s.startsWith('/') ||
              s.startsWith('photos/') ||
              s.startsWith('data:') ||
              s.startsWith('blob:'))
        );

      const thumbUrl = isDirectUrl(p.thumbnailKey) ? p.thumbnailKey! : '';
      const prevUrl = isDirectUrl(p.previewKey) ? p.previewKey! : thumbUrl;

      return {
        id: p.id,
        photo: p,
        index: idx,
        src: prevUrl || thumbUrl || '',
        msrc: thumbUrl || '',
        w: p.width && p.width > 0 ? p.width : 1600,
        h: p.height && p.height > 0 ? p.height : 1200,
        alt: p.fileName || `Foto ${idx + 1}`,
        thumbKey: p.thumbnailKey || `thumb_${p.id}.webp`,
        prevKey: p.previewKey || `prev_${p.id}.webp`,
      };
    });
  }, [sortedPhotos]);

  // Synchronize active slide state
  const handleSlideChange = useCallback(
    (newIndex: number) => {
      setCurrentIndex(newIndex);
      const photo = sortedPhotos[newIndex];
      if (photo && onPhotoChangeRef.current) {
        onPhotoChangeRef.current(photo, newIndex);
      }
    },
    [sortedPhotos]
  );

  // --------------------------------------------------------------------------
  // Pre-fetch & Swap HD Previews Progressively
  // --------------------------------------------------------------------------
  const loadHdPreviewForSlide = useCallback(
    async (slideIndex: number, pswpInstance: PhotoSwipe) => {
      if (slideIndex < 0 || slideIndex >= slideItems.length) return;
      const item = slideItems[slideIndex];
      if (!item || !item.prevKey) return;

      try {
        const hdUrl = await resolveUrl(item.prevKey);
        if (!hdUrl) return;

        activeBlobUrlsRef.current.add(hdUrl);
        item.src = hdUrl;

        // If the slide is currently rendered or active in the viewport, update DOM image directly
        const currSlide = pswpInstance.currSlide;
        if (currSlide && currSlide.index === slideIndex && currSlide.content) {
          const imgEl = currSlide.content.element as HTMLImageElement | null;
          if (imgEl && imgEl.tagName === 'IMG' && imgEl.src !== hdUrl) {
            imgEl.src = hdUrl;
          }
        }
      } catch (err) {
        console.warn(`[Lightbox] Failed to progressive load HD preview for photo ${item.id}:`, err);
      }
    },
    [slideItems, resolveUrl]
  );

  // --------------------------------------------------------------------------
  // PhotoSwipe Initialization & Lifecycle Management
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen || sortedPhotos.length === 0 || isInitializingRef.current) return;

    isInitializingRef.current = true;
    let isCancelled = false;

    const startGallery = async () => {
      // 1. Pre-resolve URL for the starting slide to prevent blank initial render
      const startItem = slideItems[resolvedStartIndex];
      if (startItem && !startItem.src) {
        const resolvedStartUrl = await resolveUrl(startItem.prevKey || startItem.thumbKey);
        if (resolvedStartUrl && !isCancelled) {
          startItem.src = resolvedStartUrl;
          startItem.msrc = resolvedStartUrl;
          activeBlobUrlsRef.current.add(resolvedStartUrl);
        }
      }

      if (isCancelled) {
        isInitializingRef.current = false;
        return;
      }

      const options: PhotoSwipeOptions = {
        dataSource: slideItems,
        index: resolvedStartIndex,
        bgOpacity: 0.94,
        spacing: 0.12,
        loop: false, // Sequential chronological travel timeline
        allowPanToNext: true,
        pinchToClose: true,
        closeOnVerticalDrag: true,
        escKey: true,
        arrowKeys: true,
        trapFocus: true,
        returnFocus: true,
        wheelToZoom: false,
        doubleTapAction: 'zoom',
        imageClickAction: 'zoom-or-close',
        bgClickAction: 'close',
        tapAction: 'toggle-controls',
        showHideAnimationType: 'zoom',
        hideAnimationDuration: 300,
        showAnimationDuration: 300,
        zoomAnimationDuration: 300,
        preload: [1, 2], // 1 slide backward, 2 slides forward
        padding: { top: 60, bottom: 90, left: 20, right: 20 },
        closeTitle: 'Chiudi (Esc)',
        zoomTitle: 'Ingrandisci',
        arrowPrevTitle: 'Foto precedente (Freccia sinistra)',
        arrowNextTitle: 'Foto successiva (Freccia destra)',
        errorMsg: 'Impossibile caricare l’anteprima HD',
      };

      const pswp = new PhotoSwipe(options);
      pswpRef.current = pswp;

      // Filter 1: CRITICAL - Enforce 400x400 thumbnail placeholder on ALL slides during horizontal swipe
      pswp.addFilter('placeholderSrc', (placeholderSrc, content) => {
        return (content.data as any)?.msrc || placeholderSrc;
      });

      // Filter 2: Calculate zoom origin bounds from map popup thumbnail element if present
      pswp.addFilter('thumbBounds', (bounds: any, _itemData: any, index: any): any => {
        if (index === resolvedStartIndex) {
          const popupImg = document.querySelector(
            '.mapboxgl-popup img, .maplibregl-popup img'
          ) as HTMLImageElement | null;
          if (popupImg) {
            const rect = popupImg.getBoundingClientRect();
            return {
              x: rect.left,
              y: rect.top,
              w: rect.width,
            };
          }
        }
        return bounds;
      });

      // Event: Intercept slide image loading to resolve OPFS/IndexedDB blob URLs on-demand
      pswp.on('contentLoadImage', async (e) => {
        const { content } = e;
        const item = content.data as any;
        if (!item.src) {
          e.preventDefault();
          try {
            const url = await resolveUrl(item.prevKey || item.thumbKey);
            if (!url || isCancelled) {
              content.onError();
              return;
            }
            item.src = url;
            activeBlobUrlsRef.current.add(url);
            const imgEl = content.element as HTMLImageElement;
            if (imgEl) {
              imgEl.src = url;
              imgEl.alt = item.alt || '';
              content.state = 'loading';
              if (imgEl.complete && imgEl.naturalWidth) {
                content.width = imgEl.naturalWidth;
                content.height = imgEl.naturalHeight;
                content.onLoaded();
              } else {
                imgEl.onload = () => {
                  if (imgEl.naturalWidth && imgEl.naturalHeight) {
                    content.width = imgEl.naturalWidth;
                    content.height = imgEl.naturalHeight;
                  }
                  content.onLoaded();
                };
                imgEl.onerror = () => {
                  content.onError();
                };
              }
            }
          } catch {
            content.onError();
          }
        }
      });

      // Event: Dynamically synchronize slide dimensions from loaded natural image
      pswp.on('loadComplete', (e) => {
        const { content, slide } = e;
        const img = content.element as HTMLImageElement | undefined;
        if (img && img.naturalWidth && img.naturalHeight) {
          if (content.width !== img.naturalWidth || content.height !== img.naturalHeight) {
            content.width = img.naturalWidth;
            content.height = img.naturalHeight;
            if (slide) {
              slide.width = img.naturalWidth;
              slide.height = img.naturalHeight;
              slide.updateContentSize(true);
            }
          }
        }
      });

      // Event: Track slide index changes & prefetch adjacent photos
      pswp.on('change', () => {
        const newIdx = pswp.currIndex;
        handleSlideChange(newIdx);

        // Pre-fetch HD previews for current and adjacent slides
        loadHdPreviewForSlide(newIdx, pswp);
        loadHdPreviewForSlide(newIdx + 1, pswp);
        loadHdPreviewForSlide(newIdx - 1, pswp);
        loadHdPreviewForSlide(newIdx + 2, pswp);
      });

      // Event: Toggle custom overlays when user taps slide
      pswp.on('tapAction', () => {
        setControlsVisible((prev) => !prev);
      });

      // Event: Close handler
      pswp.on('close', () => {
        onCloseRef.current();
      });

      // Event: Final cleanup on destruction
      pswp.on('destroy', () => {
        pswpRef.current = null;
        isInitializingRef.current = false;
      });

      // Initialize gallery
      pswp.init();

      // Immediate HD load for start slide and neighbors
      loadHdPreviewForSlide(resolvedStartIndex, pswp);
      loadHdPreviewForSlide(resolvedStartIndex + 1, pswp);
      loadHdPreviewForSlide(resolvedStartIndex - 1, pswp);
    };

    startGallery();

    // Unmount cleanup
    return () => {
      isCancelled = true;
      if (pswpRef.current) {
        try {
          if (!pswpRef.current.isDestroying) {
            pswpRef.current.destroy();
          }
        } catch {}
        pswpRef.current = null;
      }
      isInitializingRef.current = false;
    };
  }, [
    isOpen,
    sortedPhotos.length,
    resolvedStartIndex,
    slideItems,
    handleSlideChange,
    loadHdPreviewForSlide,
    resolveUrl,
  ]);

  // Programmatic slide jump helper (for Filmstrip taps)
  const handleSelectPhoto = useCallback(
    (targetIndex: number) => {
      if (pswpRef.current && targetIndex >= 0 && targetIndex < sortedPhotos.length) {
        pswpRef.current.goTo(targetIndex);
      }
    },
    [sortedPhotos.length]
  );

  if (!isOpen || sortedPhotos.length === 0) {
    return null;
  }

  const currentPhoto = sortedPhotos[currentIndex] || sortedPhotos[0];

  return (
    <div
      className={`fixed inset-0 z-[100010] pointer-events-none transition-opacity duration-200 ${
        controlsVisible ? 'opacity-100' : 'opacity-0'
      } ${className}`}
      aria-label="Controlli galleria foto"
    >
      {/* 1. Top Glassmorphic Navigation Bar */}
      <header className="absolute top-0 left-0 right-0 min-h-16 pt-[env(safe-area-inset-top,0px)] px-4 md:px-6 flex items-center justify-between bg-gradient-to-b from-slate-950/80 via-slate-950/40 to-transparent pointer-events-auto backdrop-blur-[2px]">
        {/* Left: Photo Counter & Category Pill */}
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="text-xs font-semibold tracking-wide text-slate-300 bg-slate-800/80 px-2.5 py-1 rounded-full border border-slate-700/60 shadow-sm">
            {currentIndex + 1} di {sortedPhotos.length}
          </span>
          {currentPhoto.category && (
            <span className="hidden sm:inline-block text-xs font-medium text-slate-300 bg-slate-800/60 px-2.5 py-1 rounded-full border border-slate-700/40">
              {currentPhoto.category}
            </span>
          )}
        </div>

        {/* Center: Step Location Name (if available) */}
        {currentPhoto.stepName && (
          <div className="hidden md:flex items-center text-xs font-medium text-slate-200 truncate max-w-sm">
            <span className="truncate">{currentPhoto.stepName}</span>
          </div>
        )}

        {/* Right: Actions (EXIF Info Toggle, Close) */}
        <div className="flex items-center gap-2">
          {/* EXIF Drawer Toggle */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExifDrawerOpen((prev) => !prev);
            }}
            className={`p-2 rounded-xl transition flex items-center justify-center cursor-pointer border ${
              isExifDrawerOpen
                ? 'bg-blue-600/90 text-white border-blue-500 shadow-lg shadow-blue-500/30'
                : 'bg-slate-900/70 text-slate-300 hover:text-white hover:bg-slate-800 border-slate-700/60'
            }`}
            title="Dati di scatto (EXIF)"
            aria-label="Dati di scatto EXIF"
            aria-expanded={isExifDrawerOpen}
          >
            <Info className="w-5 h-5" />
          </button>

          {/* Close Lightbox */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (pswpRef.current) {
                pswpRef.current.close();
              } else {
                onClose();
              }
            }}
            className="p-2 rounded-xl bg-slate-900/70 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700/60 transition flex items-center justify-center cursor-pointer shadow-md"
            title="Chiudi galleria (Esc)"
            aria-label="Chiudi galleria a schermo intero"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* 2. Desktop Arrow Navigation Buttons */}
      <div className="hidden md:block pointer-events-auto">
        {currentIndex > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              pswpRef.current?.prev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-slate-900/60 hover:bg-slate-900/90 text-white border border-slate-700/60 transition shadow-xl hover:scale-105 active:scale-95 cursor-pointer backdrop-blur-sm"
            aria-label="Foto precedente"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {currentIndex < sortedPhotos.length - 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              pswpRef.current?.next();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-slate-900/60 hover:bg-slate-900/90 text-white border border-slate-700/60 transition shadow-xl hover:scale-105 active:scale-95 cursor-pointer backdrop-blur-sm"
            aria-label="Foto successiva"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* 3. Slot / Default: Translucent EXIF Drawer (Explorer 2) */}
      <div className="pointer-events-auto">
        {renderExifDrawer ? (
          renderExifDrawer(currentPhoto, isExifDrawerOpen, () =>
            setIsExifDrawerOpen((p) => !p)
          )
        ) : (
          <ExifDrawer
            photo={currentPhoto}
            timezoneId={timezoneId}
            isOpen={isExifDrawerOpen}
            onClose={() => setIsExifDrawerOpen(false)}
            onLocateOnMap={onLocateOnMap}
          />
        )}
      </div>

      {/* 4. Slot / Default: Nearby Photos Filmstrip (Explorer 2) */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-auto pb-[env(safe-area-inset-bottom,0px)]">
        {renderFilmstrip ? (
          renderFilmstrip(currentIndex, sortedPhotos, handleSelectPhoto)
        ) : (
          <Filmstrip
            photos={sortedPhotos}
            currentIndex={currentIndex}
            onSelectPhoto={handleSelectPhoto}
          />
        )}
      </div>
    </div>
  );
};

export default LightboxViewer;
